import { useCallback, useEffect, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { Form, Input } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash, faKey } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import Cookies from "js-cookie";
import {
  EmailAuthProvider,
  getAuth,
  getIdToken,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import { app } from "../..";
import { useToasts } from "../../contexts/toasts";
import { useAuth } from "../../contexts/auth";

type Props = {
  show: boolean;
  onClose: () => void;
};

// Lunghezza minima imposta da Firebase Auth.
const MIN_PASSWORD_LENGTH = 6;

// Messaggi per i codici di errore di Firebase che l'utente può risolvere.
const ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-credential": "La password attuale non è corretta.",
  "auth/wrong-password": "La password attuale non è corretta.",
  "auth/weak-password": `La nuova password è troppo debole: servono almeno ${MIN_PASSWORD_LENGTH} caratteri.`,
  "auth/too-many-requests": "Troppi tentativi: riprova tra qualche minuto.",
  "auth/requires-recent-login":
    "Per sicurezza esci e accedi di nuovo prima di cambiare la password.",
  "auth/network-request-failed": "Connessione assente: riprova.",
};

const passwordIcon = (visible: boolean) => (
  <FontAwesomeIcon icon={visible ? faEyeSlash : faEye} />
);

/**
 * Cambio password del gestore. L'account è su Firebase Auth: si riautentica
 * l'utente con la password attuale (Firebase lo pretende per operazioni
 * sensibili) e poi si aggiorna la password. Il backend non è coinvolto.
 */
export default function ChangePasswordModal({ show, onClose }: Props) {
  const [form] = Form.useForm();
  const { showToast } = useToasts();
  const { setToken } = useAuth();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show) form.resetFields();
  }, [show, form]);

  const handleFinish = useCallback(
    async (values: { currentPassword: string; newPassword: string }) => {
      const auth = getAuth(app);
      setLoading(true);
      try {
        // Dopo un refresh della pagina Firebase ripristina la sessione in
        // modo asincrono: senza questa attesa currentUser può essere null.
        await auth.authStateReady();
        const user = auth.currentUser;
        if (!user || !user.email) {
          showToast(
            "change-password-error",
            "Sessione scaduta: esci e accedi di nuovo per cambiare la password.",
            "",
            "danger"
          );
          return;
        }

        const credential = EmailAuthProvider.credential(
          user.email,
          values.currentPassword
        );
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, values.newPassword);

        // Token aggiornato nel cookie, così la sessione del gestionale
        // resta allineata a quella Firebase.
        const token = await getIdToken(user, true);
        Cookies.set(window.__env.AUTH_TOKEN_NAME || "get-move-token", token);
        setToken(token);

        showToast(
          "change-password-success",
          "Password aggiornata correttamente.",
          "",
          "success"
        );
        onClose();
      } catch (error: any) {
        const message =
          ERROR_MESSAGES[error?.code] ||
          "Errore durante il cambio password. Riprova più tardi.";
        showToast("change-password-error", message, "", "danger");
      } finally {
        setLoading(false);
      }
    },
    [showToast, setToken, onClose]
  );

  return (
    <Modal show={show} onHide={loading ? undefined : onClose} centered>
      <Modal.Header closeButton={!loading}>
        <Modal.Title className="fs-5">
          <FontAwesomeIcon icon={faKey} className="text-secondary" />
          <span className="ms-2">Cambia password</span>
        </Modal.Title>
      </Modal.Header>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Modal.Body>
          <Form.Item
            name="currentPassword"
            label="Password attuale"
            rules={[
              { required: true, message: "La password attuale è obbligatoria" },
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Password attuale..."
              autoComplete="current-password"
              iconRender={passwordIcon}
            />
          </Form.Item>
          <Form.Item
            name="newPassword"
            label="Nuova password"
            dependencies={["currentPassword"]}
            rules={[
              { required: true, message: "La nuova password è obbligatoria" },
              {
                min: MIN_PASSWORD_LENGTH,
                message: `Almeno ${MIN_PASSWORD_LENGTH} caratteri`,
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("currentPassword") !== value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error("La nuova password deve essere diversa da quella attuale")
                  );
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Nuova password..."
              autoComplete="new-password"
              iconRender={passwordIcon}
            />
          </Form.Item>
          <Form.Item
            name="newPasswordConfirmation"
            label="Conferma nuova password"
            dependencies={["newPassword"]}
            rules={[
              { required: true, message: "La conferma è obbligatoria" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error("Le password non coincidono"));
                },
              }),
            ]}
          >
            <Input.Password
              size="large"
              placeholder="Conferma nuova password..."
              autoComplete="new-password"
              iconRender={passwordIcon}
            />
          </Form.Item>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="button-secondary"
            disabled={loading}
            onClick={onClose}
          >
            Annulla
          </button>
          <button
            type="submit"
            disabled={loading}
            className={classNames("button-primary", {
              "button-disabled": loading,
            })}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : (
              <>
                <FontAwesomeIcon icon={faKey} />
                <span className="ms-2">Aggiorna password</span>
              </>
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
